import { endpoints } from '@/core/shared/endpoints.config'
import ENV from '@/engine/env/web.env'
import { cookiePersistance, localPersistance, tokenPersistance } from '@wallbox/http'
import type { RepositoryHttpBuild } from '@/core/shared'
import { provideAuthUseCases } from '@/core/auth'
import {
  TRANSFER_STATUS,
  TransferEmailMismatchError,
  TransferNotActiveError,
  TransferNotFoundError,
  type Transfer,
  type TransferRepository,
  type TransferStatus
} from '@/core/transfer/domain'
import type { ApiTransferError, ApiTransferResponse } from './api'

const toStatus = (value?: string): TransferStatus | undefined => {
  return Object.values(TRANSFER_STATUS).find(status => status === value)
}

const toTransfer = (response: ApiTransferResponse): Transfer => {
  const { id, attributes } = response.data

  return {
    id,
    status: toStatus(attributes.status) ?? TRANSFER_STATUS.SENT,
    chargerSerial: attributes.charger_serial,
    siteAddress: attributes.site_address?.trim() || undefined,
    installerName: attributes.installer_name?.trim() || undefined,
    customerEmail: attributes.customer_email,
    expiresAt: attributes.expires_at
  }
}

const mapError = (error: unknown): unknown => {
  const apiError = error as ApiTransferError
  const code = apiError?.code ?? apiError?.error?.code

  if (code === 'transfer_not_found' || apiError?.status === 404) return new TransferNotFoundError()
  if (code === 'email_mismatch' || apiError?.status === 403) return new TransferEmailMismatchError()
  if (code === 'transfer_not_active' || apiError?.status === 409) {
    return new TransferNotActiveError(toStatus(apiError?.error?.meta?.transfer_status))
  }

  return error
}

export const wapiTransferRepositoryBuilder: RepositoryHttpBuild<TransferRepository> = ({ httpService }) => {
  const persistance = ENV.api.authSystem === 'cookie' ? cookiePersistance : localPersistance

  const publicInstance = httpService.create({
    baseURL: ENV.api.baseURL,
    options: {
      onLogout: () => undefined
    }
  })

  const authInstance = httpService.create({
    baseURL: ENV.api.baseURL,
    options: {
      auth: ENV.api.authSystem === 'cookie' ? httpService.AUTH_METHODS.COOKIE : httpService.AUTH_METHODS.LOCAL_STORAGE,
      onLogout: () => {
        tokenPersistance(persistance).remove()
      },
      onRefreshToken: async () => {
        const result = await provideAuthUseCases().doRefreshToken()
        return {
          token: result.token,
          refreshToken: result.refreshToken
        }
      }
    }
  })

  const url = (template: string, token: string) => template.replace('{token}', encodeURIComponent(token))

  return {
    async getTransfer (token) {
      try {
        const response = await publicInstance.get<ApiTransferResponse>(
          url(endpoints.v4.commissioning_transfers_token, token)
        )
        if (!response) throw new Error('empty transfer response')
        return toTransfer(response)
      } catch (error) {
        throw mapError(error)
      }
    },

    async acceptTransfer (token) {
      try {
        const response = await authInstance.post<ApiTransferResponse>(
          url(endpoints.v4.commissioning_transfers_token_accept, token)
        )
        return toTransfer(response.data)
      } catch (error) {
        throw mapError(error)
      }
    },

    async rejectTransfer (token) {
      try {
        const response = await authInstance.post<ApiTransferResponse>(
          url(endpoints.v4.commissioning_transfers_token_reject, token)
        )
        return toTransfer(response.data)
      } catch (error) {
        throw mapError(error)
      }
    }
  }
}
